import { useState, useEffect, useRef, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { getApiBaseUrl, getSiteProfile, isCheepSweepzHost, isNaviHost } from "./lib/site-profile";

// Global Ticker
import "./styles/live-pulse-ticker.css";
import LivePulseTicker from "./components/LivePulseTicker";

// Components
import LandingPage from "./pages/LandingPage";
import DiscoveryPage from "./pages/DiscoveryPage";
import SpotDetailPage from "./pages/SpotDetailPage";
import ExperiencesPage from "./pages/ExperiencesPage";
import ExperienceDetailPage from "./pages/ExperienceDetailPage";
import BookingsPage from "./pages/BookingsPage";
import DashboardPage from "./pages/DashboardPage";
import AssistantPage from "./pages/AssistantPage";
import AdminPage from "./pages/AdminPage";
import AuthPage from "./pages/AuthPage";
import PremiumPage from "./pages/PremiumPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import CommunityPage from "./pages/CommunityPage";
import SubmitProductPage from "./pages/SubmitProductPage";
import SubmitErrorBoundary from "./components/SubmitErrorBoundary";
import CartPage from "./pages/CartPage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";

export const API = getApiBaseUrl();

if (typeof window !== "undefined") {
  axios.defaults.headers.common["X-Site-Origin"] = window.location.origin;
  axios.defaults.headers.common["X-DHC-Hub"] = "digitalhustlerscaribbean.com";
}

// Auth Context
export const AuthContext = createContext(null);
export const DestinationContext = createContext(null);

export const useAuth = () => useContext(AuthContext);
export const useDestination = () => useContext(DestinationContext);

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);
  const siteProfile = getSiteProfile();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = location.hash;
      const sessionId = hash.split("session_id=")[1];

      if (!sessionId) {
        navigate("/");
        return;
      }

      try {
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        login(response.data.user);
        toast.success(siteProfile.welcomeToast);
        navigate("/dashboard", { state: { user: response.data.user } });
      } catch (error) {
        console.error("Auth error:", error);
        toast.error("Authentication failed");
        navigate("/auth");
      }
    };

    processSession();
  }, [location, navigate, login, siteProfile]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

const DestinationProvider = ({ children }) => {
  const [destination, setDestination] = useState("BB");
  const [currency, setCurrency] = useState("USD");
  const [shippingSpeed, setShippingSpeed] = useState("standard");
  return (
    <DestinationContext.Provider
      value={{ destination, setDestination, currency, setCurrency, shippingSpeed, setShippingSpeed }}
    >
      {children}
    </DestinationContext.Provider>
  );
};

// App Router
function AppRouter() {
  const location = useLocation();
  const cheepHost = isCheepSweepzHost();
  const naviHost = isNaviHost();

  // Check for session_id in hash - process before rendering routes
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/discover" element={<DiscoveryPage />} />
      <Route path="/spot/:spotId" element={<SpotDetailPage />} />
      <Route path="/experiences" element={<ExperiencesPage />} />
      <Route path="/experience/:experienceId" element={<ExperienceDetailPage />} />
      <Route path="/assistant" element={<AssistantPage />} />
      <Route path="/community" element={<CommunityPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/dhc-hq" element={<AdminPage />} />
      <Route path="/management" element={<AdminPage />} />
      <Route path="/bookings" element={<BookingsPage />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/premium" element={<PremiumPage />} />
      {(naviHost || cheepHost) ? (
        <>
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/:productId" element={<ProductDetailPage />} />
        </>
      ) : null}
      {cheepHost ? (
        <>
          <Route
            path="/submit"
            element={(
              <SubmitErrorBoundary>
                <SubmitProductPage />
              </SubmitErrorBoundary>
            )}
          />
          <Route path="/cart" element={<CartPage />} />
        </>
      ) : null}
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<LandingPage />} />
      <Route path="*" element={<LandingPage />} />
    </Routes>
  );
}

function App() {
  const siteProfile = getSiteProfile();

  useEffect(() => {
    document.title = siteProfile.documentTitle;
  }, [siteProfile]);

  useEffect(() => {
    const ping = () => {
      axios.post(`${API}/infrastructure/idle-sentinel`, {
        event: 'customer_activity',
        source: 'caribnavi-client',
      }).catch(() => {});
    };
    ping();
    const timer = window.setInterval(ping, 5 * 60 * 1000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <DestinationProvider>
        <LivePulseTicker
          hqBaseUrl={
            process.env.REACT_APP_DHC_HQ_URL ||
            process.env.REACT_APP_DHC_API_BASE_URL ||
            "https://digitalhustlerscaribbean.com"
          }
          siteRole={
            siteProfile.id === "cheepsweepz"
              ? "cheepsweepz"
              : siteProfile.id === "navi"
                ? "navi"
                : "dhc"
          }
          badgeLabel={
            siteProfile.id === "cheepsweepz"
              ? "CHEEPSWEEPZ"
              : siteProfile.id === "navi"
                ? "CARIBNAVI"
                : "DHC HQ"
          }
        />
        <div className="mansion-chrome-offset min-h-screen">
          <Toaster position="top-center" richColors />
          <AppRouter />
        </div>
        </DestinationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
